import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Plus, LayoutDashboard } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { LogoMark, Wordmark } from "./Logo";
import { useSite } from "../context/SiteContext";
import { useAuth } from "../context/AuthContext";

const LINKS = [
  { to: "/features", label: "Features" },
  { to: "/commands", label: "Commands" },
  { to: "/premium", label: "Premium" },
  { to: "/boosters", label: "Boosters" },
  { to: "/docs", label: "Docs" },
  { to: "/status", label: "Status" },
  { to: "/support", label: "Support" },
];

export default function Navbar() {
  const { site, botOnline } = useSite();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const invite = site?.bot_invite_url || "#";

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.35)]" data-testid="navbar">
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0 group" data-testid="nav-logo">
          <span className="transition-transform duration-500 group-hover:rotate-180 inline-block">
            <LogoMark size={30} />
          </span>
          <Wordmark className="text-lg hidden xs:inline sm:inline" />
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isActive ? "text-white bg-white/10" : "text-muted-foreground hover:text-white hover:bg-white/5"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Link to="/status" data-testid="nav-bot-status"
            className="hidden sm:inline-flex items-center gap-2 border border-white/10 hover:border-white/25 rounded-full px-3.5 py-1.5 transition-colors duration-200"
            title="Bot status — details on the status page">
            <span className="relative flex h-2 w-2">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${botOnline ? "bg-emerald-400" : "bg-red-400"}`} />
              <span className={`relative inline-flex rounded-full h-2 w-2 ${botOnline ? "bg-emerald-400" : "bg-red-400"}`} />
            </span>
            <span className={`text-[11px] font-mono uppercase tracking-wider ${botOnline ? "text-white/50" : "text-red-400"}`}>
              {botOnline ? "Bot online" : "Bot offline"}
            </span>
          </Link>
          {user ? (
            <Link to="/admin" data-testid="nav-staff-panel"
              className="hidden sm:inline-flex items-center gap-2 border border-volt/40 text-volt hover:bg-volt/10 text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-200">
              <LayoutDashboard size={14} /> Staff Panel
            </Link>
          ) : (
            <Link to="/login" data-testid="nav-login"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-white transition-colors duration-200 px-2">
              Log in
            </Link>
          )}
          <a
            href={invite}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-add-to-discord"
            className="hidden sm:inline-flex items-center gap-2 bg-blurple hover:bg-[#4752c4] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200 shadow-[0_0_24px_rgba(88,101,242,0.45)]"
          >
            <Plus size={16} strokeWidth={3} /> Add to Discord
          </a>
          <button
            className="md:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden overflow-hidden border-t border-white/10 bg-black/80"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                  className={`py-3 text-base font-medium border-b border-white/5 ${location.pathname === l.to ? "text-blurple" : "text-white/80"}`}
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-login"
                className="py-3 text-base font-medium border-b border-white/5 text-white/80"
              >
                Log in / Staff
              </Link>
              <a
                href={invite}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-add-to-discord"
                className="mt-3 inline-flex items-center justify-center gap-2 bg-blurple text-white font-semibold px-5 py-3 rounded-full"
              >
                <Plus size={16} strokeWidth={3} /> Add to Discord
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
